import React from "react";

/**
 * PaginatedTable
 * --------------
 * Tiny controlled, server-side paginated table.
 *
 * Props
 *   columns       : Array<{ name: string, label: string, render?: (row) => ReactNode, align?: string, width?: string }>
 *   rows          : Array<object>           current page rows
 *   page          : number                  current 1-based page
 *   limit         : number                  current page size
 *   total         : number                  total row count across all pages
 *   loading       : boolean                 show a loading row
 *   onPageChange  : (page: number) => void
 *   onLimitChange : (limit: number) => void
 *   emptyMessage  : string                  shown when total === 0 and not loading
 *   pageSizeOptions : number[]              defaults to [10, 25, 50, 100]
 *
 * Notes
 * - This is intentionally NOT MUIDataTable. The rest of the FE uses
 *   MUIDataTable in client-side mode; for the Credits ledger and the
 *   Load Credit page we need server-side pagination, so we render plain
 *   Bootstrap markup that already exists in the project's nicepage UI.
 */
const PaginatedTable = ({
  columns,
  rows,
  page,
  limit,
  total,
  loading,
  onPageChange,
  onLimitChange,
  emptyMessage,
  pageSizeOptions,
}) => {
  const sizes =
    pageSizeOptions && pageSizeOptions.length > 0
      ? pageSizeOptions
      : [10, 25, 50, 100];

  const safeTotal = Number(total) || 0;
  const safePage = Number(page) || 1;
  const safeLimit = Number(limit) || 10;
  const totalPages = Math.max(1, Math.ceil(safeTotal / safeLimit));
  const currentPage = Math.min(safePage, totalPages);

  const start = safeTotal === 0 ? 0 : (currentPage - 1) * safeLimit + 1;
  const end = Math.min(currentPage * safeLimit, safeTotal);

  // Compact sliding window for page numbers.
  const buildPages = () => {
    const result = [];
    const windowSize = 5;
    let from = Math.max(1, currentPage - 2);
    let to = Math.min(totalPages, from + windowSize - 1);
    from = Math.max(1, to - windowSize + 1);
    for (let p = from; p <= to; p += 1) result.push(p);
    return result;
  };

  const goTo = (p) => {
    if (p < 1 || p > totalPages || p === currentPage) return;
    if (typeof onPageChange === "function") onPageChange(p);
  };

  const onSizeChange = (e) => {
    const next = parseInt(e.target.value, 10);
    if (typeof onLimitChange === "function") onLimitChange(next);
  };

  return (
    <div className="paginated-table">
      <div className="table-responsive">
        <table className="table table-bordered table-sm">
          <thead className="thead-light">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.name}
                  style={{
                    textAlign: col.align || "left",
                    width: col.width || "auto",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  <span className="spinner-border spinner-border-sm" />
                  <span style={{ marginLeft: "8px" }}>Loading…</span>
                </td>
              </tr>
            ) : rows && rows.length > 0 ? (
              rows.map((row, idx) => (
                <tr key={row.id != null ? `r-${row.id}` : `r-${idx}`}>
                  {columns.map((col) => (
                    <td
                      key={col.name}
                      style={{
                        textAlign: col.align || "left",
                        fontSize: "13px",
                        verticalAlign: "middle",
                      }}
                    >
                      {typeof col.render === "function"
                        ? col.render(row)
                        : row[col.name] != null
                          ? String(row[col.name])
                          : ""}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: "center", padding: "20px", color: "#8094ae" }}
                >
                  {emptyMessage || "No records found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div
        className="d-flex justify-content-between align-items-center"
        style={{ flexWrap: "wrap", gap: "8px", marginTop: "8px" }}
      >
        <div style={{ fontSize: "13px", color: "#526484" }}>
          {safeTotal === 0
            ? "No records"
            : `Showing ${start}–${end} of ${safeTotal}`}
        </div>

        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
          <label style={{ fontSize: "13px", margin: 0 }}>
            Per page:&nbsp;
            <select
              className="form-control form-control-sm"
              style={{ display: "inline-block", width: "auto" }}
              value={safeLimit}
              onChange={onSizeChange}
              disabled={loading}
            >
              {sizes.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>

          <ul
            className="pagination pagination-sm"
            style={{ margin: 0, flexWrap: "wrap" }}
          >
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => goTo(1)}
                disabled={currentPage === 1 || loading}
              >
                «
              </button>
            </li>
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => goTo(currentPage - 1)}
                disabled={currentPage === 1 || loading}
              >
                ‹
              </button>
            </li>

            {buildPages().map((p) => (
              <li
                key={`p-${p}`}
                className={`page-item ${p === currentPage ? "active" : ""}`}
              >
                <button
                  type="button"
                  className="page-link"
                  onClick={() => goTo(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              </li>
            ))}

            <li
              className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
            >
              <button
                type="button"
                className="page-link"
                onClick={() => goTo(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
              >
                ›
              </button>
            </li>
            <li
              className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
            >
              <button
                type="button"
                className="page-link"
                onClick={() => goTo(totalPages)}
                disabled={currentPage === totalPages || loading}
              >
                »
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PaginatedTable;
